import { Tune } from "@mui/icons-material";
import { Box, SxProps, Theme } from "@mui/material";

// Constants for cloud provider types
export const CLOUD_PROVIDER_TYPES = {
	AZURE_SUBSCRIPTION: "AzureSubscription",
	AWS_ACCOUNT: "AwsAccount",
	GOOGLE_CLOUD_PROJECT: "GoogleCloudProject",
} as const;

export type CloudProviderType = (typeof CLOUD_PROVIDER_TYPES)[keyof typeof CLOUD_PROVIDER_TYPES];

// Portal-owned branding is packaged by copy-devicons at prestart/prebuild, never fetched from a CDN by the browser.
const CLOUD_PROVIDER_ASSET_BASE = `${process.env.PUBLIC_URL || ''}/devicons`;
export const CLOUD_PROVIDER_ICON_URLS = {
	AZURE: `${CLOUD_PROVIDER_ASSET_BASE}/azure/azure-original.svg`,
	AWS: `${CLOUD_PROVIDER_ASSET_BASE}/amazonwebservices/amazonwebservices-original-wordmark.svg`,
	GCP: `${CLOUD_PROVIDER_ASSET_BASE}/googlecloud/googlecloud-original.svg`,
};

type CloudProviderLogoIconProps = {
	sx?: SxProps<Theme>;
	titleAccess?: string;
};

function CloudProviderLogoIcon({
	src,
	alt,
	titleAccess,
	sx,
}: CloudProviderLogoIconProps & {
	src: string;
	alt: string;
}) {
	return (
		<Box
			component="img"
			src={src}
			alt={alt}
			title={titleAccess}
			sx={[
				{
					width: 18,
					height: 18,
					display: "inline-block",
					objectFit: "contain",
					flexShrink: 0,
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		/>
	);
}

export function AzureCloudIcon(props: CloudProviderLogoIconProps) {
	return <CloudProviderLogoIcon {...props} src={CLOUD_PROVIDER_ICON_URLS.AZURE} alt="Azure" />;
}

export function AwsCloudIcon(props: CloudProviderLogoIconProps) {
	return <CloudProviderLogoIcon {...props} src={CLOUD_PROVIDER_ICON_URLS.AWS} alt="AWS" />;
}

export function GcpCloudIcon(props: CloudProviderLogoIconProps) {
	return <CloudProviderLogoIcon {...props} src={CLOUD_PROVIDER_ICON_URLS.GCP} alt="Google Cloud" />;
}

export type CloudProviderDefinition = {
	type: CloudProviderType;
	tabLabel: string;
	connectionLabel: string;
	emptyConnectionLabel: string;
	Icon: React.ComponentType<CloudProviderLogoIconProps>;
	providerAttribute: Record<string, string>;
};

export const CLOUD_PROVIDER_DEFINITIONS: CloudProviderDefinition[] = [
	{
		type: CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION,
		tabLabel: "Azure Cloud",
		connectionLabel: "Azure Subscription",
		emptyConnectionLabel: "Azure subscription",
		Icon: AzureCloudIcon,
		providerAttribute: { "Azure:Provider": "Azure" },
	},
	{
		type: CLOUD_PROVIDER_TYPES.AWS_ACCOUNT,
		tabLabel: "AWS Cloud",
		connectionLabel: "AWS Account",
		emptyConnectionLabel: "AWS account",
		Icon: AwsCloudIcon,
		providerAttribute: { "Amazon:Provider": "AWS" },
	},
	{
		type: CLOUD_PROVIDER_TYPES.GOOGLE_CLOUD_PROJECT,
		tabLabel: "Google Cloud",
		connectionLabel: "Google Cloud Project",
		emptyConnectionLabel: "Google Cloud project",
		Icon: GcpCloudIcon,
		providerAttribute: { "Google:Provider": "GoogleCloud" },
	},
];

export function getCloudProviderDefinition(type: string | undefined) {
	return CLOUD_PROVIDER_DEFINITIONS.find((provider) => provider.type === type);
}

export function getEnvironmentCloudProvider(attributes?: { [key: string]: any } | null) {
	if (!attributes) {
		return undefined;
	}

	const keys = Object.keys(attributes);
	if (keys.some((key) => key.startsWith("Azure:"))) {
		return getCloudProviderDefinition(CLOUD_PROVIDER_TYPES.AZURE_SUBSCRIPTION);
	}
	if (keys.some((key) => key.startsWith("Amazon:") || key.startsWith("AWS:"))) {
		return getCloudProviderDefinition(CLOUD_PROVIDER_TYPES.AWS_ACCOUNT);
	}
	if (keys.some((key) => key.startsWith("Google:") || key.startsWith("Gcp:") || key.startsWith("GCP:"))) {
		return getCloudProviderDefinition(CLOUD_PROVIDER_TYPES.GOOGLE_CLOUD_PROJECT);
	}

	return undefined;
}

// Azure regions options
export const AZURE_REGIONS = [
	{ value: "eastus", label: "East US" },
	{ value: "eastus2", label: "East US 2" },
	{ value: "eastus3", label: "East US 3" }, // new / recently launched :contentReference[oaicite:2]{index=2}
	{ value: "centralus", label: "Central US" },
	{ value: "northcentralus", label: "North Central US" },
	{ value: "southcentralus", label: "South Central US" },
	{ value: "westus", label: "West US" },
	{ value: "westus2", label: "West US 2" },
	{ value: "westus3", label: "West US 3" }, // new / recently launched :contentReference[oaicite:3]{index=3}
	{ value: "westcentralus", label: "West Central US" },

	{ value: "canadacentral", label: "Canada Central" },
	{ value: "canadaeast", label: "Canada East" },

	{ value: "brazilsouth", label: "Brazil South" },
	{ value: "brazilsoutheast", label: "Brazil Southeast" }, // added :contentReference[oaicite:4]{index=4}

	{ value: "northEurope", label: "North Europe" },
	{ value: "westeurope", label: "West Europe" },
	{ value: "francecentral", label: "France Central" },
	{ value: "francesouth", label: "France South" }, // added :contentReference[oaicite:5]{index=5}
	{ value: "germanywestcentral", label: "Germany West Central" },
	{ value: "germanynorth", label: "Germany North" }, // added :contentReference[oaicite:6]{index=6}
	{ value: "norwayeast", label: "Norway East" },
	{ value: "norwaywest", label: "Norway West" }, // added :contentReference[oaicite:7]{index=7}
	{ value: "swedencentral", label: "Sweden Central" }, // added :contentReference[oaicite:8]{index=8}
	{ value: "switzerlandnorth", label: "Switzerland North" },
	{ value: "switzerlandwest", label: "Switzerland West" }, // added :contentReference[oaicite:9]{index=9}
	{ value: "spaincentral", label: "Spain Central" }, // added :contentReference[oaicite:10]{index=10}
	{ value: "italynorth", label: "Italy North" }, // added :contentReference[oaicite:11]{index=11}
	{ value: "uksouth", label: "UK South" },
	{ value: "ukwest", label: "UK West" },

	{ value: "eastasia", label: "East Asia" },
	{ value: "southeastasia", label: "Southeast Asia" },

	{ value: "australiaeast", label: "Australia East" },
	{ value: "australiasoutheast", label: "Australia Southeast" },
	{ value: "australiacentral", label: "Australia Central" }, // added :contentReference[oaicite:12]{index=12}
	{ value: "australiacentral2", label: "Australia Central 2" }, // added :contentReference[oaicite:13]{index=13}

	{ value: "centralindia", label: "Central India" },
	{ value: "southindia", label: "South India" },
	{ value: "westindia", label: "West India" },

	{ value: "japaneast", label: "Japan East" },
	{ value: "japanwest", label: "Japan West" },

	{ value: "koreacentral", label: "Korea Central" },
	{ value: "koreasouth", label: "Korea South" },

	{ value: "malaysiawest", label: "Malaysia West" }, // added :contentReference[oaicite:14]{index=14}
	{ value: "indonesiacentral", label: "Indonesia Central" }, // added :contentReference[oaicite:15]{index=15}

	{ value: "newzealandnorth", label: "New Zealand North" }, // added :contentReference[oaicite:16]{index=16}

	{ value: "polandcentral", label: "Poland Central" }, // added :contentReference[oaicite:17]{index=17}

	{ value: "uaenorth", label: "UAE North" }, // added :contentReference[oaicite:18]{index=18}
	{ value: "uaecentral", label: "UAE Central" }, // added :contentReference[oaicite:19]{index=19}

	{ value: "southafricanorth", label: "South Africa North" },
	{ value: "southafricawest", label: "South Africa West" }, // added :contentReference[oaicite:20]{index=20}

	{ value: "israelcentral", label: "Israel Central" }, // added :contentReference[oaicite:21]{index=21}

	{ value: "qatarcentral", label: "Qatar Central" }, // added :contentReference[oaicite:22]{index=22}
];

// Configuration for attribute prefixes and their corresponding icons
export type PrefixConfig = {
	icon: React.ComponentType<CloudProviderLogoIconProps>;
	color: string;
	tooltip: string;
};

export const ATTRIBUTE_PREFIX_CONFIG: Record<string, PrefixConfig> = {
	"Azure:": {
		icon: AzureCloudIcon,
		color: "#0078d4",
		tooltip: "Azure attribute",
	},
	"AWS:": {
		icon: AwsCloudIcon,
		color: "#ff9900",
		tooltip: "AWS attribute",
	},
	"Amazon:": {
		icon: AwsCloudIcon,
		color: "#ff9900",
		tooltip: "AWS attribute",
	},
	"GCP:": {
		icon: GcpCloudIcon,
		color: "#4285f4",
		tooltip: "Google Cloud attribute",
	},
	"Gcp:": {
		icon: GcpCloudIcon,
		color: "#4285f4",
		tooltip: "Google Cloud attribute",
	},
	"Google:": {
		icon: GcpCloudIcon,
		color: "#4285f4",
		tooltip: "Google Cloud attribute",
	},
	"Custom:": {
		icon: Tune,
		color: "#9c27b0",
		tooltip: "Custom attribute",
	},
};
